import type { Media, Persisted } from '@tunasong/schemas'
import { useCallback, useEffect, useEffectEvent, useState } from 'react'
import { getDurationLabel } from '../duration/index.js'
import type { PlayerProps } from './audio-player.js'
import { useAudioPlayer } from './audio-player.js'

interface PlayerControl extends PlayerProps {
  el: HTMLAudioElement | null
  name?: string
  active?: Pick<Persisted<Media>, 'id' | 'name' | 'storageUrls'>
  media: Pick<Persisted<Media>, 'id' | 'name' | 'storageUrls'>[]
  /**
   * When false, a track's `ended` event does NOT advance to the next track —
   * playback stops on the current entry and waits for explicit user input.
   * Defaults to true (pre-existing continuous-playlist behavior). Used by the
   * Playlist Performance Mode, where autoplaying the next song mid-gig is
   * never acceptable.
   */
  autoAdvance?: boolean
  onSelect(idx: number): void
}

/**
 * The `ended` events that already advanced the playlist. Every useGlobalPlayer runs a controller on the one shared
 * element, and each sees the active track the previous one just set: one track end must advance exactly once.
 */
const advancedOn = new WeakSet<Event>()

/**
 * How many `autoAdvance: false` controllers are mounted per element. Any one of them stops track end for all: the
 * app-wide command bridge advances by default and its listener runs before Playlist Performance Mode's.
 */
const advanceHolds = new WeakMap<HTMLMediaElement, number>()

export const usePlayerControl = (props: PlayerControl) => {
  const { active, onSelect, el, media, autoAdvance = true, ...restProps } = props

  const audioPlayer = useAudioPlayer({
    el: el ?? null,
    ...restProps,
  })

  const activeIdx = active ? media.findIndex(m => m.id === active.id) : -1
  const hasPrev = activeIdx > 0
  const hasNext = activeIdx >= 0 && activeIdx < media.length - 1

  const handlePrev = useCallback(() => {
    if (!hasPrev) {
      return
    }
    onSelect(activeIdx - 1)
  }, [activeIdx, hasPrev, onSelect])

  const handleNext = useCallback(() => (hasNext ? onSelect(activeIdx + 1) : undefined), [activeIdx, hasNext, onSelect])

  /** The Audio element may not be ready to play when we invoke play(). Hence, we set autoPlay to start when it's ready */
  const [autoPlay, setAutoPlay] = useState(false)

  /**
   * Effect events, so the listeners below stay attached across renders and still see this render's state. A
   * listener re-attached by a commit that lands mid-dispatch misses the event: GlobalAudioPlayer's own `ended`
   * listener writes the player store, re-rendering this hook between native listeners, and the track never advanced.
   */
  const handleEnded = useEffectEvent((event: Event) => {
    if (!autoAdvance || (el && (advanceHolds.get(el) ?? 0) > 0)) {
      // Performance-mode semantics: stop on the current entry and clear the
      // pending-autoplay latch so a later `canplay` doesn't restart audio.
      setAutoPlay(false)
      return
    }
    if (hasNext && !advancedOn.has(event)) {
      advancedOn.add(event)
      setAutoPlay(true)
      handleNext()
    }
  })

  // Trigger a start when we're already playing
  const handleStart = useEffectEvent(() => {
    if (autoPlay) {
      void audioPlayer.play()
    }
  })

  const play = useCallback(async () => {
    setAutoPlay(true)
    return audioPlayer.play()
  }, [audioPlayer])

  const pause = useCallback(() => {
    setAutoPlay(false)
    audioPlayer.pause()
  }, [audioPlayer])

  const togglePlay = useCallback(() => {
    // If we're paused, toggle will play.
    if (!audioPlayer.playing) {
      setAutoPlay(true)
    }
    void audioPlayer.togglePlay()
  }, [audioPlayer])

  const [seeking, setSeeking] = useState(false)
  useEffect(() => {
    if (!el || autoAdvance) {
      return
    }
    advanceHolds.set(el, (advanceHolds.get(el) ?? 0) + 1)
    return () => {
      advanceHolds.set(el, (advanceHolds.get(el) ?? 1) - 1)
    }
  }, [el, autoAdvance])

  /** Events from the player */
  useEffect(() => {
    if (!el) {
      return
    }
    const handleSetSeeking = () => setSeeking(true)
    const handleClearSeeking = () => setSeeking(false)
    const handleEndedEvent = (event: Event) => handleEnded(event)
    const handleCanPlay = () => handleStart()

    el.addEventListener('seeking', handleSetSeeking)
    el.addEventListener('seeked', handleClearSeeking)
    el.addEventListener('ended', handleEndedEvent)
    el.addEventListener('canplay', handleCanPlay)

    return () => {
      el.removeEventListener('seeking', handleSetSeeking)
      el.removeEventListener('seeked', handleClearSeeking)
      el.removeEventListener('ended', handleEndedEvent)
      el.removeEventListener('canplay', handleCanPlay)
      // Intentionally no setSeeking(false) here. This effect used to re-run
      // on every render, and a setState in its cleanup pushed an
      // enqueueConcurrentHookUpdate per render — after ~50 renders, React's
      // nestedUpdateCount tripped and raised "Maximum update depth exceeded"
      // (TUNASONG-VP / GH #1338). The listeners are already removed above,
      // so no further 'seeking' / 'seeked' events can fire on this element;
      // there's nothing to reset.
    }
  }, [el])

  return {
    ...audioPlayer,
    play,
    pause,
    togglePlay,
    progressLabel: `${getDurationLabel(el?.currentTime ?? 0)}/${getDurationLabel(audioPlayer.duration)}`,
    prev: hasPrev ? handlePrev : undefined,
    next: hasNext ? handleNext : undefined,
    seeking,
  }
}
